import * as React from 'react'
import * as plume from '@ulule/owl-kit-components/next'

import { Container } from '../../_shared/Container'
import { SectionHead, SectionHeadHighlight } from '../../_shared/SectionHead'
import { formatInteger } from '../../_shared/ululeStats'
import { ALL_CATEGORIES, useCategoryFilter } from '../categoryFilter'
import { type CategoryBoard, type Collection } from '../collections'
import { POPULAR_COLLECTIONS, TOP_COLLECTIONS } from '../content'
import { useStatsTracking } from '../tracking'
import { CategorySelect } from './CategorySelect'
import { Leaderboard, type LeaderboardEntry } from './Leaderboard'
import { PopularGrid } from './PopularGrid'
import styles from './Boards.module.css'

const FILTER_ID = 'top10-category'

/**
 * What sits under a project's name.
 *
 * On the unfiltered board that is its category. On a filtered one the category is already in the
 * heading, and the row's own label can even contradict it: `tag_id:` matches any of a project's
 * tags, so eight of the ten biggest Lieux & Commerces collections are primarily Solidaire &
 * Citoyen or Patrimoine, and a KissKissBankBank row carries a label from KKBB's own taxonomy —
 * "Livre & Édition" under an option reading "Édition & Média". So a filtered row says where the
 * collection happened instead, which is the one thing the filter does not.
 */
function caption(collection: Collection, filtered: boolean): string {
  if (!filtered) return collection.category

  return collection.fromKkbb === true ? TOP_COLLECTIONS.kkbbPlatform : ''
}

/**
 * A presale sells products rather than collecting towards a goal, so its revenue is sales and not a
 * collection amount: the row shows what it pre-sold instead. It still ranks on the amount, which is
 * the only figure comparable across the board.
 *
 * Owners who asked for it count "contributions" rather than "préventes"; that follows the project's
 * own setting, exactly as its public page does.
 */
function toTopEntry(collection: Collection, index: number, filtered: boolean): LeaderboardEntry {
  const entry = {
    rank: index + 1,
    name: collection.name,
    url: collection.url,
    caption: caption(collection, filtered),
    imageUrl: collection.imageUrl,
  }

  if (!collection.isPresale) {
    return { ...entry, value: `${formatInteger(collection.amountEur)} €` }
  }

  const noun = collection.usesContributionWording ? 'contribution' : 'prévente'
  const plural = collection.preSalesCount > 1 ? 's' : ''

  return {
    ...entry,
    value: `${formatInteger(collection.preSalesCount)} ${noun}${plural}`,
    valueLabel: collection.usesContributionWording ? 'Contributions :' : 'Préventes :',
  }
}

export type TopCollectionsProps = {
  /** The unfiltered board, and what the default option selects. */
  topAllTime: Collection[]
  /** One board per category, in the site's category order. Empty leaves the filter off the page. */
  byCategory: CategoryBoard[]
}

/** Biggest collections of all time, Ulule and KissKissBankBank combined, filterable by category. */
export function TopCollections({ topAllTime, byCategory }: TopCollectionsProps): React.ReactElement | null {
  const tracking = useStatsTracking()
  const [requested, setCategory] = useCategoryFilter()

  // No board at all rather than a partial one: see `buildTopAllTime`.
  if (topAllTime.length === 0) return null

  // A `?cat=` naming no board — a category dropped from the site, or a typo — falls back to the
  // default, so the control never carries a value none of its options hold.
  const categoryBoard = byCategory.find((board) => board.slug === requested)
  const selected = categoryBoard?.slug ?? ALL_CATEGORIES
  const entries = categoryBoard?.entries ?? topAllTime
  const selectedLabel = categoryBoard?.label ?? TOP_COLLECTIONS.allCategories

  const options = [
    { value: ALL_CATEGORIES, label: TOP_COLLECTIONS.allCategories },
    ...byCategory.map((board) => ({ value: board.slug, label: board.label })),
  ]

  function selectCategory(slug: string): void {
    setCategory(slug)
    tracking.topFilterChange(slug)
  }

  return (
    <section className={styles.top} id="top10">
      <Container>
        <div className={styles.top__head}>
          <SectionHead
            size="wide"
            className={styles.top__headText}
            kicker={TOP_COLLECTIONS.kicker}
            title={
              <>
                <SectionHeadHighlight>Top 10</SectionHeadHighlight>
                {' des collectes de tous les temps'}
              </>
            }
            lede={TOP_COLLECTIONS.lede}
          />
          {byCategory.length > 0 && (
            <CategorySelect
              id={FILTER_ID}
              label={TOP_COLLECTIONS.filterLabel}
              options={options}
              value={selected}
              onChange={selectCategory}
            />
          )}
        </div>
        {/* The board is deliberately outside the live region: replacing ten rows inside one makes
            a screen reader read all ten out. This announces the change in a line instead. It also
            fires once just after hydration on a `?cat=` deep link, since the prerendered HTML can
            only ever name the default board. */}
        {byCategory.length > 0 && (
          <p className={styles.top__status} aria-live="polite">
            {`${TOP_COLLECTIONS.filterLabel} : ${selectedLabel}, ${entries.length} collecte${entries.length > 1 ? 's' : ''}`}
          </p>
        )}
        <Leaderboard
          entries={entries.map((collection, index) => toTopEntry(collection, index, categoryBoard !== undefined))}
          valueLabel="Montant collecté :"
          onEntryClick={(rank) => tracking.projectClick('all_time', rank, selected)}
        />
      </Container>
    </section>
  )
}

/** Currently-funding Ulule collections, the five with the most contributors. */
export function PopularCollections({ collections }: { collections: Collection[] }): React.ReactElement | null {
  const tracking = useStatsTracking()

  if (collections.length === 0) return null

  return (
    <section className={styles.popular}>
      <Container>
        <SectionHead
          size="wide"
          kicker={POPULAR_COLLECTIONS.kicker}
          title={
            <>
              {'Les collectes les plus '}
              <SectionHeadHighlight>populaires</SectionHeadHighlight>
              {' du moment'}
            </>
          }
        />
        <PopularGrid collections={collections} />
        <div className={styles.popular__more}>
          <plume.RoundButton
            as="a"
            href={POPULAR_COLLECTIONS.cta.href}
            color="black"
            size="big"
            onClick={tracking.discoverClick}
          >
            {POPULAR_COLLECTIONS.cta.label}
          </plume.RoundButton>
        </div>
      </Container>
    </section>
  )
}
